import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import RoadCareCalculator from '../../../../components/RoadCareCalculator';
import {
  getContractCoverages,
  getRiskAmounts,
  setTransportUsageType,
} from '../../../../redux/modules/roadCareCalculation';
import { ACTION_NAME, APPLICATION_CONTRACT_TYPES, RISK_AMOUNT_TYPES } from '../../../../constants';
import { ROAD_CARE_PLAN_TYPES } from '../../../../constants/roadCare';
import Navigation from './Navigation';
import InfoBlock from '../../../../components/InfoBlock';
import { roadCareCalculationAgreement } from '../../../../validations/contract/roadCare';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import {
  clearCalculationBackendErrors,
  saveDraftContract,
  saveRoadCareCareContractInCache,
  setHasAgreed,
} from '../../../../redux/modules/roadCareContract';
import { getTransportUsageTypes } from '../../../../redux/modules/vehicles';
import ScrollToTopPage from '../../../../components/ScrollToTopPage';

const CalculationStep = ({ params, goPrevious, blockNextSteps, goNext }) => {
  const {
    setStepNavigationBox,
    isDeviceLgSize,
    handleOpenMobileSummary,
    totalPrice,
    isFetchingRiskAmounts,
    loader,
    hasAgreed,
  } = params;
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const isSavingCache = useSelector((state) => state.roadCareContract.savingContractInCache);

  const {
    register,
    setValue,
    errors,
    getValues,
    watch,
    triggerValidation,
    handleSubmit,
  } = useForm({
    defaultValues: {
      hasAgreed,
    },
    mode: 'onBlur',
    validationSchema: roadCareCalculationAgreement,
  });

  useRegisterValidationFields(['hasAgreed'], register);
  watch('hasAgreed');

  useEffect(() => {
    dispatch(getTransportUsageTypes(APPLICATION_CONTRACT_TYPES.ROAD_CARE));
    dispatch(getRiskAmounts(ACTION_NAME.CONTRACT, RISK_AMOUNT_TYPES.AUTO_CARE));
    dispatch(getContractCoverages(
      ACTION_NAME.CONTRACT,
      APPLICATION_CONTRACT_TYPES.ROAD_CARE,
      ROAD_CARE_PLAN_TYPES.DEFAULT,
    ));
  }, [dispatch]);

  const dispatchTransportUsageType = useCallback((selectedTransportUsageType) => {
    dispatch(setTransportUsageType(selectedTransportUsageType, ACTION_NAME.CONTRACT));
  }, [dispatch]);

  const next = useCallback(() => {
    triggerValidation();
    dispatch(clearCalculationBackendErrors());
    if (isAuthenticated) {
      dispatch(saveDraftContract());
    }
    dispatch(saveRoadCareCareContractInCache()).then(
      () => goNext(),
      () => { },
    );
  }, [dispatch, goNext, isAuthenticated, triggerValidation]);

  const handleNext = useCallback(() => {
    handleSubmit(next)();
  }, [handleSubmit, next]);

  if (isFetchingRiskAmounts) {
    return <>{loader}</>;
  }

  return (
    <>
      {isSavingCache && loader}
      <ScrollToTopPage withDelay />
      <RoadCareCalculator
        showForm={false}
        dispatchTransportUsageType={dispatchTransportUsageType}
        showNavigationButtons={false}
      />
      <InfoBlock
        formProps={{
          register,
          errors,
        }}
        handleAgreementClick={(event) => {
          const { checked } = event.target;
          setValue('hasAgreed', checked);
          dispatch(setHasAgreed(checked));
          if (!checked) {
            blockNextSteps();
          }
        }}
        hasAgreed={getValues().hasAgreed}
        agreementText="roadCareContract.calculationPageAgreement"
      />
      <Navigation
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        handleOpenMobileSummary={handleOpenMobileSummary}
        handleNext={handleNext}
        isFormValid={getValues().hasAgreed}
        goPrevious={goPrevious}
        totalPrice={totalPrice}
      />
    </>
  );
};

CalculationStep.propTypes = {
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    isDeviceLgSize: PropTypes.bool.isRequired,
    isFetchingRiskAmounts: PropTypes.bool.isRequired,
    hasAgreed: PropTypes.bool.isRequired,
    totalPrice: PropTypes.number.isRequired,
    loader: PropTypes.node.isRequired,
  }).isRequired,
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
};

export default CalculationStep;
